import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom'; // Для редиректа при выходе
import Layout from './Layout';
import { Table, Input, Button, DatePicker, Space, Typography, Tag, Select, Row, Col, Card, Avatar, Dropdown, Menu } from 'antd';
import { SearchOutlined, SyncOutlined, UserOutlined, LogoutOutlined, DownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const UserTasksPage = () => {
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [allTasks, setAllTasks] = useState([]); // Храним все загруженные задачи для фильтрации
    const [loading, setLoading] = useState(false);
    const [userCounterparties, setUserCounterparties] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);

    // Состояния фильтров
    const [searchText, setSearchText] = useState('');
    const [dateRange, setDateRange] = useState([null, null]);
    const [selectedStages, setSelectedStages] = useState([]);
    const [selectedWorkTypes, setSelectedWorkTypes] = useState([]);
    const [selectedAssignees, setSelectedAssignees] = useState([]);

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

    // 1. Загрузка данных пользователя
    useEffect(() => {
        const fetchUserData = async () => {
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('userId');
            const userStr = localStorage.getItem('user');

            if (userStr) {
                try {
                    setCurrentUser(JSON.parse(userStr));
                } catch (e) { console.error("Ошибка парсинга user из storage", e); }
            }

            if (!userId) return;

            try {
                const userRes = await axios.get(`/api/users/${userId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const details = userRes.data.counterparties_details || [];
                const names = details.map(cp => cp.name);
                setUserCounterparties(names);
            } catch (error) {
                console.error("Ошибка загрузки данных пользователя:", error);
            }
        };

        fetchUserData();
    }, []);

    // 2. Загрузка задач
    const fetchTasks = async () => {
        if (userCounterparties.length === 0) return;

        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            // Запрашиваем много задач для клиентской фильтрации
            const response = await axios.get('/api/tasks', {
                params: { limit: 2000 },
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const loadedTasks = response.data || [];
            setAllTasks(loadedTasks); // Сохраняем сырые данные

        } catch (error) {
            console.error("Ошибка загрузки задач:", error);
        } finally {
            setLoading(false);
        }
    };

    // Вызываем загрузку при появлении контрагентов
    useEffect(() => {
        if (userCounterparties.length > 0) {
            fetchTasks();
        }
    }, [userCounterparties]);

    // Хелперы для извлечения данных
    const getCustomField = (record, fieldName) => {
        try {
            const raw = record.raw_data;
            let cf = raw?.custom_fields;
            if (typeof cf === 'string') cf = JSON.parse(cf);
            return cf?.[fieldName]?.value || '-';
        } catch (e) { return '-'; }
    };

    const getSafeRawValue = (record, path) => {
        try {
            const raw = record.raw_data || {};
            return path.split('.').reduce((acc, part) => acc && acc[part], raw) || '-';
        } catch { return '-'; }
    }

    // 3. Эффект фильтрации (срабатывает при изменении allTasks или фильтров)
    useEffect(() => {
        if (allTasks.length === 0) {
            setTasks([]);
            return;
        }

        const filtered = allTasks.filter(task => {
            // 1. Проверка по контрагенту (Безопасность)
            let taskCustomer = null;
            try {
                const raw = typeof task.raw_data === 'string' ? JSON.parse(task.raw_data) : task.raw_data;
                if (raw && raw.custom_fields) {
                    const cf = typeof raw.custom_fields === 'string' ? JSON.parse(raw.custom_fields) : raw.custom_fields;
                    taskCustomer = cf.Zakazchik?.value || cf.Zakazchik;
                }
            } catch (e) {}

            if (!taskCustomer || !userCounterparties.includes(taskCustomer)) {
                return false;
            }

            // 2. Текстовый поиск (ID, Заголовок, Номер БС, Номер ТТ)
            if (searchText) {
                const lowSearch = searchText.toLowerCase();

                const matchId = String(task.external_id).includes(lowSearch);
                const matchTitle = (task.title || '').toLowerCase().includes(lowSearch);

                // Извлекаем кастомные поля для поиска
                const bsNumber = getCustomField(task, 'Nomer_BS').toLowerCase();
                const ttNumber = getCustomField(task, 'Nomer_TT').toLowerCase();

                const matchBS = bsNumber !== '-' && bsNumber.includes(lowSearch);
                const matchTT = ttNumber !== '-' && ttNumber.includes(lowSearch);

                if (!matchId && !matchTitle && !matchBS && !matchTT) return false;
            }

            // 3. Дата
            if (dateRange[0] && dateRange[1]) {
                const taskDate = dayjs(task.news_date || task.created_at);
                if (taskDate.isBefore(dateRange[0]) || taskDate.isAfter(dateRange[1])) return false;
            }

            // 4. Фильтр по Этапу
            if (selectedStages.length > 0) {
                const stage = getSafeRawValue(task, 'status_name');
                if (!selectedStages.includes(stage)) return false;
            }

            // 5. Фильтр по Виду работ
            if (selectedWorkTypes.length > 0) {
                const workType = task.news_type_name || getSafeRawValue(task, 'type.name');
                if (!selectedWorkTypes.includes(workType)) return false;
            }

            // 6. Фильтр по Исполнителю
            if (selectedAssignees.length > 0) {
                const assignee = task.assigned_user_fio || getSafeRawValue(task, 'assignee.name');
                if (!selectedAssignees.includes(assignee)) return false;
            }

            return true;
        });

        setTasks(filtered);
        setPagination(prev => ({ ...prev, current: 1 })); // Сброс на 1 страницу при фильтрации

    }, [allTasks, searchText, dateRange, selectedStages, selectedWorkTypes, selectedAssignees, userCounterparties]);


    // --- ОПЦИИ ДЛЯ ФИЛЬТРОВ (вычисляются из отфильтрованных по контрагенту задач) ---
    const availableTasks = useMemo(() => {
        if (!allTasks.length || !userCounterparties.length) return [];
        return allTasks.filter(task => {
            let taskCustomer = null;
            try {
                const raw = typeof task.raw_data === 'string' ? JSON.parse(task.raw_data) : task.raw_data;
                if (raw && raw.custom_fields) {
                    const cf = typeof raw.custom_fields === 'string' ? JSON.parse(raw.custom_fields) : raw.custom_fields;
                    taskCustomer = cf.Zakazchik?.value || cf.Zakazchik;
                }
            } catch (e) {}
            return taskCustomer && userCounterparties.includes(taskCustomer);
        });
    }, [allTasks, userCounterparties]);

    const stageOptions = useMemo(() => {
        const options = new Set(availableTasks.map(t => getSafeRawValue(t, 'status_name')).filter(Boolean));
        return Array.from(options).sort().map(v => ({ label: v, value: v }));
    }, [availableTasks]);

    const workTypeOptions = useMemo(() => {
        const options = new Set(availableTasks.map(t => t.news_type_name || getSafeRawValue(t, 'type.name')).filter(Boolean));
        return Array.from(options).sort().map(v => ({ label: v, value: v }));
    }, [availableTasks]);

    const assigneeOptions = useMemo(() => {
        const options = new Set(availableTasks.map(t => t.assigned_user_fio || getSafeRawValue(t, 'assignee.name')).filter(Boolean));
        return Array.from(options).sort().map(v => ({ label: v, value: v }));
    }, [availableTasks]);


    const handleLogout = () => {
        localStorage.clear();
        navigate('/login');
    };

    // Меню пользователя
    const userMenu = (
        <Menu>
            <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout}>
                Выйти
            </Menu.Item>
        </Menu>
    );

    const columns = [
        {
            title: '№',
            key: 'index',
            width: 50,
            render: (text, record, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: 'ID',
            dataIndex: 'external_id',
            key: 'external_id',
            width: 80,
            render: (id) => <Text strong>{id}</Text>
        },
        {
            title: 'Дата создания',
            dataIndex: 'news_date',
            key: 'news_date',
            width: 130,
            render: (date) => date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '-',
            sorter: (a, b) => dayjs(a.news_date).unix() - dayjs(b.news_date).unix(),
            defaultSortOrder: 'descend'
        },
        {
            title: 'Заголовок',
            dataIndex: 'title',
            key: 'title',
            ellipsis: true,
            width: 200,
        },
        {
            title: 'Текст задания',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            width: 250
        },
        {
            title: 'Вид работы',
            key: 'news_type_name',
            dataIndex: 'news_type_name',
            width: 150,
        },
        {
            title: 'Этап',
            key: 'status_name',
            width: 120,
            render: (_, record) => <Tag color="blue">{getSafeRawValue(record, 'status_name')}</Tag>
        },
        {
            title: 'Исполнитель',
            dataIndex: 'assigned_user_fio',
            key: 'assigned_user_fio',
            width: 150,
        },
        {
            title: 'Номер БС',
            key: 'bs_number',
            width: 100,
            render: (_, record) => getCustomField(record, 'Nomer_BS')
        },
        {
            title: 'Номер ТТ',
            key: 'tt_number',
            width: 100,
            render: (_, record) => getCustomField(record, 'Nomer_TT')
        },
        {
            title: 'Прибыл',
            key: 'arrival_time',
            width: 120,
            render: (_, record) => getCustomField(record, 'Vremja_pojavlenija_na_sayte')
        },
        {
            title: 'Убыл',
            key: 'leave_time',
            width: 120,
            render: (_, record) => getCustomField(record, 'Vremja_pokidanija_sayta')
        },
        {
            title: 'Описание работ',
            key: 'work_desc',
            width: 200,
            ellipsis: true,
            render: (_, record) => getCustomField(record, 'Opisanie_vepolnenneh_rabot')
        },
    ];

    return (
        <Layout>
            <div style={{ padding: '24px', margin: '0 auto', maxWidth: 1800 }}>

                {/* --- HEADER --- */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 20,
                    background: '#fff',
                    padding: '15px 20px',
                    borderRadius: 8,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}>
                    <div>
                        <Title level={3} style={{ margin: 0 }}>Мои Задачи</Title>
                        <div style={{ marginTop: 5 }}>
                            <Text type="secondary">Доступ к задачам контрагентов: </Text>
                            {userCounterparties.length > 0 ?
                                userCounterparties.map(c => <Tag key={c} color="geekblue">{c}</Tag>) :
                                <Tag color="red">Нет привязок</Tag>
                            }
                        </div>
                    </div>

                    {currentUser && (
                        <Dropdown overlay={userMenu} trigger={['click']}>
                            <Button type="text" size="large" style={{ height: 'auto', padding: '5px 10px' }}>
                                <Space>
                                    <Avatar style={{ backgroundColor: '#87d068' }} icon={<UserOutlined />} />
                                    <div style={{ textAlign: 'left', lineHeight: '1.2' }}>
                                        <div style={{ fontWeight: 'bold' }}>{`${currentUser.last_name} ${currentUser.first_name}`}</div>
                                        <div style={{ fontSize: '0.8em', color: '#888' }}>@{currentUser.login}</div>
                                    </div>
                                    <DownOutlined />
                                </Space>
                            </Button>
                        </Dropdown>
                    )}
                </div>

                {/* --- FILTERS --- */}
                <Card style={{ marginBottom: 20, borderRadius: 8 }}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={8} lg={6}>
                            <Input
                                placeholder="Поиск по ID, Заголовку, БС, ТТ"
                                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                                allowClear
                            />
                        </Col>
                        <Col xs={24} md={8} lg={6}>
                            <RangePicker
                                value={dateRange}
                                onChange={setDateRange}
                                placeholder={['Дата с', 'Дата по']}
                                style={{ width: '100%' }}
                            />
                        </Col>
                        <Col xs={24} md={8} lg={6}>
                            <Select
                                mode="multiple"
                                placeholder="Фильтр по Этапу"
                                value={selectedStages}
                                onChange={setSelectedStages}
                                options={stageOptions}
                                style={{ width: '100%' }}
                                maxTagCount="responsive"
                                allowClear
                            />
                        </Col>
                        <Col xs={24} md={8} lg={6}>
                            <Select
                                mode="multiple"
                                placeholder="Фильтр по Виду работ"
                                value={selectedWorkTypes}
                                onChange={setSelectedWorkTypes}
                                options={workTypeOptions}
                                style={{ width: '100%' }}
                                maxTagCount="responsive"
                                allowClear
                            />
                        </Col>
                        <Col xs={24} md={8} lg={6}>
                            <Select
                                mode="multiple"
                                placeholder="Фильтр по Исполнителю"
                                value={selectedAssignees}
                                onChange={setSelectedAssignees}
                                options={assigneeOptions}
                                style={{ width: '100%' }}
                                maxTagCount="responsive"
                                allowClear
                            />
                        </Col>
                        <Col xs={24} md={4} lg={2} style={{ display: 'flex', alignItems: 'center' }}>
                            <Button
                                type="primary"
                                icon={<SyncOutlined />}
                                onClick={fetchTasks}
                                loading={loading}
                                block
                            >
                                Обновить
                            </Button>
                        </Col>
                    </Row>
                </Card>

                <Table
                    dataSource={tasks}
                    columns={columns}
                    rowKey="external_id"
                    loading={loading}
                    pagination={{
                        ...pagination,
                        onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} задач`
                    }}
                    scroll={{ x: 1800 }}
                    bordered
                    size="middle"
                />
            </div>
        </Layout>
    );
};

export default UserTasksPage;